import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { Field, BTN, StatusBadge } from "../Common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const EMPTY = { code: "", name: "", annual_quota: 12, accrual: "yearly", carry_forward: false, carry_forward_max: 0, expiry_months: 0, unlimited: false, paid: true, active: true };

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 text-sm"><Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />{label}</label>
);

const RuleDialog = ({ open, onOpenChange, rule, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (open) setF(rule ? { ...EMPTY, ...rule } : EMPTY);
  }, [open, rule]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, annual_quota: Number(f.annual_quota) || 0, carry_forward_max: Number(f.carry_forward_max) || 0, expiry_months: parseInt(f.expiry_months, 10) || 0 };
    try {
      if (rule) await api.put(`/leave-types/${rule.id}`, body);
      else await api.post("/leave-types", body);
      toast.success("Leave rule saved");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-rule-dialog">
      <DialogHeader><DialogTitle>{rule ? `Edit ${rule.name}` : "New leave type"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="rule-name-input" /></Field>
          <Field label="Code"><Input disabled={!!rule} placeholder="e.g. maternity" value={f.code} onChange={(e) => set("code", e.target.value)} data-testid="rule-code-input" /></Field>
        </div>
        <Toggle label="Unlimited (no balance, e.g. unpaid leave)" checked={f.unlimited} onChange={(v) => set("unlimited", v)} testid="rule-unlimited-switch" />
        {!f.unlimited && <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Yearly allowance (days)"><Input type="number" min="0" step="0.5" value={f.annual_quota} onChange={(e) => set("annual_quota", e.target.value)} data-testid="rule-quota-input" /></Field>
            <Field label="Credit">
              <Select value={f.accrual} onValueChange={(v) => set("accrual", v)}>
                <SelectTrigger data-testid="rule-accrual-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yearly">All at start of year</SelectItem><SelectItem value="monthly">Monthly (1/12 each month)</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Credits expire after (months, 0 = never)"><Input type="number" min="0" value={f.expiry_months} onChange={(e) => set("expiry_months", e.target.value)} data-testid="rule-expiry-input" /></Field>
          <Toggle label="Carry forward unused days to next year" checked={f.carry_forward} onChange={(v) => set("carry_forward", v)} testid="rule-carry-switch" />
          {f.carry_forward && <Field label="Maximum days carried forward"><Input type="number" min="0" step="0.5" value={f.carry_forward_max} onChange={(e) => set("carry_forward_max", e.target.value)} data-testid="rule-carry-max-input" /></Field>}
        </>}
        <Toggle label="Paid leave (unpaid leave reduces salary)" checked={f.paid} onChange={(v) => set("paid", v)} testid="rule-paid-switch" />
        <Toggle label="Active (employees can apply)" checked={f.active} onChange={(v) => set("active", v)} testid="rule-active-switch" />
        <Button type="submit" className={`w-full ${BTN}`} data-testid="rule-save-button">Save rule</Button>
      </form>
    </DialogContent></Dialog>
  );
};

export const LeaveRulesTab = () => {
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState({ open: false, rule: null });
  const load = () => api.get("/leave-types").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div data-testid="leave-rules-tab">
      <div className="flex justify-between items-center mb-4 gap-2">
        <p className="text-sm text-[#6E6E73]">Unused days lapse at year end unless carried forward. Expiry removes credited days after the set number of months.</p>
        <Button onClick={() => setDlg({ open: true, rule: null })} className={BTN} data-testid="add-leave-type-button"><Plus className="h-4 w-4 mr-1" />Leave type</Button>
      </div>
      <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
        <Table data-testid="leave-rules-table">
          <TableHeader><TableRow><TableHead>Leave type</TableHead><TableHead>Allowance</TableHead><TableHead>Credit</TableHead><TableHead>Carry forward</TableHead><TableHead>Expiry</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{list.map((t) => (
            <TableRow key={t.id} data-testid={`leave-rule-row-${t.code}`}>
              <TableCell><div className="font-semibold">{t.name}</div><div className="text-xs text-[#6E6E73]">{t.code}</div></TableCell>
              <TableCell>{t.unlimited ? "Unlimited" : `${t.annual_quota} days/yr`}</TableCell>
              <TableCell className="capitalize">{t.unlimited ? "—" : t.accrual}</TableCell>
              <TableCell>{t.carry_forward ? `Up to ${t.carry_forward_max}` : "No"}</TableCell>
              <TableCell>{t.expiry_months ? `${t.expiry_months} months` : "Never"}</TableCell>
              <TableCell>{t.paid ? "Yes" : "No"}</TableCell>
              <TableCell><StatusBadge status={t.active ? "active" : "inactive"} /></TableCell>
              <TableCell><Button size="icon" variant="ghost" onClick={() => setDlg({ open: true, rule: t })} data-testid={`edit-leave-rule-${t.code}`}><Pencil className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <RuleDialog open={dlg.open} rule={dlg.rule} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </div>
  );
};
